import { useCallback, useEffect, useRef, useState } from 'react'
import { api, type BranchSeat, type Campaign, type PlaylistSummary, type SignageMedia, type SignageScreen } from '../lib/api'
import { explain } from '../lib/staffErrors'
import { useStaffText } from '../ui/StaffText'

// What the Signage tabs read, loaded once and shared: each tab shows where the others' things are used.

export type SignagePart = 'media' | 'playlists' | 'campaigns' | 'screens'

export interface SignageData {
  media: SignageMedia[]
  usedBytes: number
  limitBytes: number
  playlists: PlaylistSummary[]
  campaigns: Campaign[]
  branches: BranchSeat[]
  today: string
  screens: SignageScreen[]
  /** Each part once its first answer came. */
  loaded: Record<SignagePart, boolean>
  /** Why each part's last read failed; absent when it did not. */
  faults: Partial<Record<SignagePart, string>>
  reload: (...parts: SignagePart[]) => Promise<void>
  /** A file uploaded here, shown before the next read. */
  addMedia: (m: SignageMedia) => void
}

const ALL: SignagePart[] = ['media', 'playlists', 'campaigns', 'screens']

export function useSignageData(): SignageData {
  const { lang } = useStaffText()
  const [media, setMedia] = useState<SignageMedia[]>([])
  const [space, setSpace] = useState({ used: 0, limit: 0 })
  const [playlists, setPlaylists] = useState<PlaylistSummary[]>([])
  const [campaigns, setCampaigns] = useState<{ campaigns: Campaign[]; branches: BranchSeat[]; today: string }>({ campaigns: [], branches: [], today: '' })
  const [screens, setScreens] = useState<SignageScreen[]>([])
  const [loaded, setLoaded] = useState<Record<SignagePart, boolean>>({ media: false, playlists: false, campaigns: false, screens: false })
  const [faults, setFaults] = useState<Partial<Record<SignagePart, string>>>({})
  const live = useRef(true)
  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  const reload = useCallback(
    async (...asked: SignagePart[]) => {
      const parts = asked.length > 0 ? asked : ALL
      const read: Record<SignagePart, () => Promise<void>> = {
        media: async () => {
          const r = await api.signageMedia()
          if (!live.current) return
          setMedia(r.media)
          setSpace({ used: r.usedBytes, limit: r.limitBytes })
        },
        playlists: async () => {
          const r = await api.playlists()
          if (live.current) setPlaylists(r.playlists)
        },
        campaigns: async () => {
          const r = await api.campaigns()
          if (live.current) setCampaigns(r)
        },
        screens: async () => {
          const r = await api.signageScreens()
          if (live.current) setScreens(r.screens)
        },
      }
      const results = await Promise.allSettled(parts.map((p) => read[p]()))
      if (!live.current) return
      setLoaded((l) => ({ ...l, ...Object.fromEntries(parts.map((p) => [p, true])) }))
      setFaults((f) => {
        const next = { ...f }
        results.forEach((r, i) => {
          if (r.status === 'rejected') next[parts[i]!] = explain(r.reason, lang, 'common.unreachable')
          else delete next[parts[i]!]
        })
        return next
      })
    },
    [lang],
  )

  useEffect(() => {
    void reload()
  }, [reload])

  const addMedia = useCallback((m: SignageMedia) => setMedia((list) => [m, ...list.filter((x) => x.id !== m.id)]), [])

  return {
    media,
    usedBytes: space.used,
    limitBytes: space.limit,
    playlists,
    campaigns: campaigns.campaigns,
    branches: campaigns.branches,
    today: campaigns.today,
    screens,
    loaded,
    faults,
    reload,
    addMedia,
  }
}
